import type { ButtonProps } from '../Button/index.ts';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { PiXBold } from 'react-icons/pi';
import { FormattedMessage } from 'react-intl';
import { buttonCommonI18n } from '@_/i18n-common';
import { Button } from '../Button/index.ts';
import { Text } from '../Text/index.ts';

interface NotificationProps {
  heading?: React.ReactNode;
  icon?: React.ReactNode;
  message?: React.ReactNode;
  onClose: () => void;
  open: boolean;
  primaryAction?: {
    label: React.ReactNode;
  } & Pick<ButtonProps, 'color' | 'disabled' | 'onClick'>;
  secondaryAction?: {
    label: React.ReactNode;
  } & Pick<ButtonProps, 'color' | 'disabled' | 'onClick'>;
}

export function Notification({
  heading,
  icon,
  message,
  onClose,
  open,
  primaryAction,
  secondaryAction
}: NotificationProps) {
  const hasActions = !!(primaryAction || secondaryAction);
  const [isMounted, setIsMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setIsMounted(true);
    }
  }, [open]);

  if (!isMounted) {
    return null;
  }

  return (
    <div
      className={clsx(
        'pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 motion-reduce:duration-1',
        open
          ? 'animate-in duration-300 ease-out fade-in-0 slide-in-from-bottom-2 sm:slide-in-from-bottom-0 sm:slide-in-from-right-2'
          : 'animate-out duration-200 ease-in fade-out-0 fill-mode-forwards'
      )}
      onAnimationEnd={event => {
        if (!open && event.target === event.currentTarget) {
          setIsMounted(false);
        }
      }}
    >
      <div className="p-4">
        <div className={clsx('flex', message ? 'items-start' : 'items-center')}>
          {icon && <div className="mr-3 shrink-0 *:size-6">{icon}</div>}

          <div className="w-0 flex-1">
            <Text block semibold>
              {heading}
            </Text>

            {message && (
              <Text block className="mt-1">
                {message}
              </Text>
            )}

            {hasActions && (
              <div className="mt-3 flex gap-x-7">
                {primaryAction && (
                  <Button
                    color="primary"
                    {...primaryAction}
                    onClick={primaryAction.onClick || onClose}
                    variant="plain"
                  >
                    {primaryAction.label || <FormattedMessage {...buttonCommonI18n.dismiss} />}
                  </Button>
                )}

                <Button
                  {...secondaryAction}
                  onClick={primaryAction?.onClick || onClose}
                  variant="plain"
                >
                  {secondaryAction?.label || <FormattedMessage {...buttonCommonI18n.dismiss} />}
                </Button>
              </div>
            )}
          </div>

          <div className="ml-4 flex shrink-0">
            <Button
              icon={<PiXBold />}
              onClick={() => {
                onClose();
              }}
              variant="plain"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
